/**
 * Care Emotional Baseline Widget
 *
 * Captures pre-move emotional state: anxiety level, family confidence,
 * concern areas, and support needs. Used in the Care Assessment CoPilot Guide.
 */

import type { WidgetProps } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, CardContent, cn } from '@tonnie/ui';
import { useCallback } from 'react';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';

interface BaselineConfig {
  title?: string;
  description?: string;
  variableName?: string;
}

// 5-point mood scale, struggling \u2192 amazing.
const MOOD_OPTIONS = [
  { score: 1, emoji: '\ud83d\ude14', labelKey: 'care.mood.struggling' },
  { score: 2, emoji: '\ud83d\ude10', labelKey: 'care.mood.okay' },
  { score: 3, emoji: '\ud83d\ude42', labelKey: 'care.mood.good' },
  { score: 4, emoji: '\ud83d\ude0a', labelKey: 'care.mood.great' },
  { score: 5, emoji: '\ud83e\udd29', labelKey: 'care.mood.amazing' },
];

const CONCERN_OPTIONS = [
  { id: 'housing', labelKey: 'care.concern.housing' },
  { id: 'schooling', labelKey: 'care.concern.schooling' },
  { id: 'work', labelKey: 'care.concern.work' },
  { id: 'language', labelKey: 'care.concern.language' },
  { id: 'loneliness', labelKey: 'care.concern.loneliness' },
  { id: 'family', labelKey: 'care.concern.family' },
  { id: 'finances', labelKey: 'care.concern.finances' },
  { id: 'healthcare', labelKey: 'care.concern.healthcare' },
];

type CareBaselineWidgetProps = WidgetProps & { config?: BaselineConfig };

export function CareEmotionalBaselineWidget({ config, onComplete }: CareBaselineWidgetProps) {
  const { t } = useTranslation();
  // Multi-question baseline form — every selection is real user work.
  // Persist all four fields so navigating away mid-form doesn't lose
  // their answers.
  const [persisted, patchPersisted] = useWidgetState<{
    anxietyLevel: number | null;
    familyConfidence: number | null;
    concerns: string[];
    supportNeed: number | null;
  }>('care_emotional_baseline', {
    anxietyLevel: null,
    familyConfidence: null,
    concerns: [],
    supportNeed: null,
  });
  const { anxietyLevel, familyConfidence, concerns, supportNeed } = persisted;
  const setAnxietyLevel = useCallback(
    (v: number | null) => patchPersisted({ anxietyLevel: v }),
    [patchPersisted]
  );
  const setFamilyConfidence = useCallback(
    (v: number | null) => patchPersisted({ familyConfidence: v }),
    [patchPersisted]
  );
  const setSupportNeed = useCallback(
    (v: number | null) => patchPersisted({ supportNeed: v }),
    [patchPersisted]
  );

  const toggleConcern = useCallback(
    (id: string) => {
      const next = concerns.includes(id) ? concerns.filter((c) => c !== id) : [...concerns, id];
      patchPersisted({ concerns: next });
    },
    [concerns, patchPersisted]
  );

  const canSubmit = anxietyLevel !== null && familyConfidence !== null && supportNeed !== null;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    const varName = config?.variableName ?? 'emotionalBaseline';
    onComplete?.({
      [varName]: {
        anxietyLevel,
        familyConfidence,
        concerns,
        supportNeed,
      },
    });
  }, [canSubmit, anxietyLevel, familyConfidence, concerns, supportNeed, config, onComplete]);

  return (
    <Card className="border-tone-rose-line" data-testid="widget-care_emotional_baseline">
      <CardContent className="pt-6 space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-1">
            {config?.title ?? t('care.baseline.title')}
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            {config?.description ?? t('care.baseline.desc')}
          </p>
        </div>

        {/* Anxiety level */}
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('care.baseline.anxiety')}</label>
          <div className="flex gap-2 justify-center">
            {MOOD_OPTIONS.map((opt) => (
              <button
                key={opt.score}
                onClick={() => setAnxietyLevel(opt.score)}
                data-testid={`widget-care_emotional_baseline-mood-${opt.score}`}
                className={cn(
                  'flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all min-w-[64px]',
                  anxietyLevel === opt.score
                    ? 'border-tone-rose-solid bg-tone-rose-subtle scale-105'
                    : 'border-border hover:border-tone-rose-line'
                )}
              >
                <span className="text-2xl">{opt.emoji}</span>
                <span className="text-2xs text-muted-foreground">{t(opt.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Family confidence */}
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('care.baseline.familyConfidence')}</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                onClick={() => setFamilyConfidence(v)}
                data-testid={`widget-care_emotional_baseline-family-${v}`}
                className={cn(
                  'flex-1 h-10 rounded-lg border-2 font-semibold text-sm transition-all',
                  familyConfidence === v
                    ? 'border-tone-rose-solid bg-tone-rose-solid text-tone-rose-on-solid'
                    : 'border-border hover:border-tone-rose-line'
                )}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-2xs text-muted-foreground px-1">
            <span>{t('care.baseline.notConfident')}</span>
            <span>{t('care.baseline.veryConfident')}</span>
          </div>
        </div>

        {/* Concern areas */}
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {t('care.baseline.concerns')}
            <span className="text-xs text-muted-foreground ml-1">
              ({t('care.baseline.selectMultiple')})
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            {CONCERN_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => toggleConcern(opt.id)}
                data-testid={`widget-care_emotional_baseline-tag-${opt.id}`}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                  concerns.includes(opt.id)
                    ? 'border-tone-rose-solid bg-tone-rose-subtle text-tone-rose'
                    : 'border-border text-muted-foreground hover:border-tone-rose-line'
                )}
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Support need */}
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('care.baseline.supportNeed')}</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                onClick={() => setSupportNeed(v)}
                data-testid={`widget-care_emotional_baseline-support-${v}`}
                className={cn(
                  'flex-1 h-10 rounded-lg border-2 font-semibold text-sm transition-all',
                  supportNeed === v
                    ? 'border-tone-rose-solid bg-tone-rose-solid text-tone-rose-on-solid'
                    : 'border-border hover:border-tone-rose-line'
                )}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-2xs text-muted-foreground px-1">
            <span>{t('care.baseline.minimal')}</span>
            <span>{t('care.baseline.significant')}</span>
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="widget-care_emotional_baseline-confirm"
          className="w-full bg-tone-rose-solid hover:bg-tone-rose-solid-hover text-tone-rose-on-solid"
        >
          {t('common.continue')}
        </Button>
      </CardContent>
    </Card>
  );
}
